import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import { supabase } from '../lib/supabase';
import { tenantService } from '../services/tenantService';
import { getDefaultLanding } from '../lib/defaultLanding';
import { buildTenantLoginUrl } from '../lib/tenantUrls';

interface ProtectedRouteProps {
  allowedRoles?: UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ allowedRoles }) => {
  const { user, isLoading, tenantMismatch, logout } = useAuth();
  const location = useLocation();
  const [redirecting, setRedirecting] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState<boolean | null>(null);

  // Check if user must change password (admin-created accounts).
  // Two mechanisms, both needed:
  //   1. getSession() read on mount/user change — covers initial page load.
  //   2. onAuthStateChange subscription — when the ChangePassword page clears
  //      the flag, Supabase fires USER_UPDATED and we flip state to false
  //      *before* that page's success-navigate to /admin renders. A
  //      per-navigation re-read is NOT enough: the render-time <Navigate>
  //      below (line ~110) bounces off stale state before any async effect
  //      result can land, so the user gets stuck on /change-password.
  //      (AuthContext deliberately swallows USER_UPDATED for `user` itself —
  //      the loadedUserIdRef dedup guard — so we must listen here.)
  useEffect(() => {
    if (!user) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      setMustChangePassword(session?.user?.user_metadata?.must_change_password === true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) return;
      setMustChangePassword(session.user.user_metadata?.must_change_password === true);
    });
    return () => subscription.unsubscribe();
  }, [user]);

  // On tenant mismatch: look up the correct tenant slug, log out, and redirect
  useEffect(() => {
    if (!tenantMismatch || !user || redirecting) return;

    const redirect = async () => {
      setRedirecting(true);
      try {
        const slug = await tenantService.getTenantSlug(user.tenantId);

        await logout();

        const url = slug ? buildTenantLoginUrl(slug) : null;
        if (url) {
          window.location.href = url;
        }
        // On localhost or if lookup fails, logout already happened — they'll land on /login
      } catch {
        await logout();
      }
    };

    redirect();
  }, [tenantMismatch, user]);

  if (isLoading || redirecting || (user && mustChangePassword === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (tenantMismatch) {
    // Brief flash while the redirect effect kicks in
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Force password change for admin-created users
  if (mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDefaultLanding(user.role)} replace />;
  }

  return <Outlet />;
};